import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * Homepage Section 3 — The LaneRunner Platform.
 * Left: copy + a 2×2 grid of capability cards.
 * Right: an isometric "platform stage" — an elliptical white plinth with four
 * teal capability panels standing on it, a back arc of workflow chips, dashed
 * orbital paths with teal nodes, and decorative outline circles.
 *
 * The stage is built on a fixed 900 × 820 design canvas and scaled per
 * breakpoint so geometry and overlaps stay identical at every width.
 */

/* ── inline icons (stage + cards) ─────────────────────────────── */

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const IUsers: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <path d="M16 5.6a3.2 3.2 0 0 1 0 6.3M18 19c0-2.4-.9-4.2-2.4-5.3" />
  </svg>
);
const IGlobe: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17M12 3.5c2.4 2.4 2.4 14.6 0 17M12 3.5c-2.4 2.4-2.4 14.6 0 17" />
  </svg>
);
const IBars: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <path d="M4 20h16" />
    <path d="M7.5 20v-6M12 20V8M16.5 20v-9" />
  </svg>
);
const ITruck: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <path d="M2.5 16.5V7.5h11v9z" />
    <path d="M13.5 16.5h4l4-4v-2h-8z" />
    <circle cx="7" cy="18.5" r="1.8" />
    <circle cx="17" cy="18.5" r="1.8" />
  </svg>
);
const ISend: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <path d="M21 4L3.5 11l7 2.4L13 21z" />
    <path d="M21 4l-10.5 9.4" />
  </svg>
);
const ITag: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <path d="M12.5 3.5H20V11l-8.5 8.5-8-8z" />
    <circle cx="16.4" cy="7.6" r="1.3" />
  </svg>
);
const IAward: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <circle cx="12" cy="9.5" r="5.5" />
    <path d="M9 14.5L7.5 21l4.5-2.2L16.5 21 15 14.5" />
  </svg>
);
const IDoc: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <path d="M6.5 3.5h7L18 8v12.5h-11z" />
    <path d="M13.5 3.5V8H18M9.5 12h6M9.5 15.5h4" />
  </svg>
);
const IPin: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} {...stroke}>
    <path d="M12 21s6-5.6 6-10a6 6 0 1 0-12 0c0 4.4 6 10 6 10z" />
    <circle cx="12" cy="11" r="2.3" />
  </svg>
);

/* ── stage ────────────────────────────────────────────────────── */

type Chip = { label: string[]; icon: React.ReactNode; x: number; y: number; rot: number; w: number; h: number };

const chips: Chip[] = [
  { label: ["Post", "Freight"], icon: <ISend className="h-5 w-5" />, x: 0, y: 168, rot: -3, w: 116, h: 138 },
  { label: ["Private", "Network"], icon: <IUsers className="h-5 w-5" />, x: 126, y: 118, rot: -2, w: 116, h: 134 },
  { label: ["Open", "Marketplace"], icon: <IGlobe className="h-5 w-5" />, x: 258, y: 100, rot: 0, w: 122, h: 130 },
  { label: ["Compare", "Quotes"], icon: <IBars className="h-5 w-5" />, x: 396, y: 108, rot: 2, w: 118, h: 132 },
  { label: ["Clear", "Pricing"], icon: <ITag className="h-5 w-5" />, x: 528, y: 138, rot: 4, w: 114, h: 134 },
  { label: ["Award", "Load"], icon: <IAward className="h-5 w-5" />, x: 650, y: 182, rot: 6, w: 112, h: 136 },
  { label: ["Dispatch", "& Docs"], icon: <IDoc className="h-5 w-5" />, x: 762, y: 244, rot: 9, w: 110, h: 136 },
  { label: ["Track", "Delivery"], icon: <IPin className="h-5 w-5" />, x: 860, y: 330, rot: 12, w: 108, h: 136 },
];

type Panel = { label: string[]; icon: React.ReactNode; x: number; y: number; w: number; h: number; skew: number };

const panels: Panel[] = [
  { label: ["Private", "Networks"], icon: <IUsers className="h-9 w-9" />, x: 30, y: 320, w: 132, h: 300, skew: -7 },
  { label: ["Open", "Marketplace"], icon: <IGlobe className="h-9 w-9" />, x: 186, y: 372, w: 136, h: 320, skew: -4 },
  { label: ["Quote", "Comparison"], icon: <IBars className="h-9 w-9" />, x: 346, y: 418, w: 138, h: 330, skew: -1 },
  { label: ["Shipment", "Management"], icon: <ITruck className="h-9 w-9" />, x: 508, y: 460, w: 140, h: 336, skew: 2 },
];

const Stage: React.FC = () => (
  <div className="relative" style={{ width: 980, height: 830 }}>
    {/* plinth + orbital paths + decorative circles */}
    <svg viewBox="0 0 980 830" className="absolute inset-0 h-full w-full" fill="none" aria-hidden>
      <defs>
        <linearGradient id="plinthTop" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#EDF3F5" />
        </linearGradient>
        <linearGradient id="plinthSide" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#E7EEF1" />
          <stop offset="1" stopColor="#D8E3E7" />
        </linearGradient>
        <radialGradient id="plinthGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#02857E" stopOpacity="0.10" />
          <stop offset="1" stopColor="#02857E" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx="500" cy="560" rx="430" ry="200" fill="url(#plinthGlow)" />

      {/* plinth body (side + top) */}
      <path
        d="M70 520a430 178 0 0 0 860 0v46a430 178 0 0 1-860 0z"
        fill="url(#plinthSide)"
      />
      <ellipse cx="500" cy="520" rx="430" ry="178" fill="url(#plinthTop)" />
      <ellipse cx="500" cy="520" rx="430" ry="178" stroke="#E2EAEE" strokeWidth="1.5" />
      {/* inner raised ring */}
      <ellipse cx="500" cy="500" rx="286" ry="112" fill="#FFFFFF" stroke="#E6EDF0" strokeWidth="1.5" />
      <ellipse cx="500" cy="492" rx="248" ry="94" fill="#F5F9FA" stroke="#EAF0F2" strokeWidth="1.4" />

      {/* dashed orbital paths */}
      <g stroke="#17E5DD" strokeOpacity="0.55" strokeWidth="1.6" strokeDasharray="5 8">
        <path d="M60 430C-10 560 120 700 320 730c210 32 430-8 540-70" />
        <path d="M120 300C40 200 120 120 250 110" strokeOpacity="0.3" />
        <path d="M700 130c180 20 250 130 210 250" strokeOpacity="0.4" />
      </g>
      {/* nodes on the front path */}
      {[
        [172, 636],
        [258, 690],
        [400, 726],
        [552, 736],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <circle cx={x} cy={y} r="9" fill="#17E5DD" />
          <circle cx={x} cy={y} r="3.4" fill="#FFFFFF" />
        </g>
      ))}
      <g>
        <circle cx="912" cy="596" r="8" fill="none" stroke="#17E5DD" strokeWidth="2" />
        <circle cx="912" cy="596" r="2.6" fill="#17E5DD" />
      </g>

      {/* decorative outline circles */}
      <g stroke="#17E5DD" strokeOpacity="0.45">
        <circle cx="884" cy="76" r="62" strokeWidth="1.4" />
        <circle cx="742" cy="60" r="4" fill="#17E5DD" stroke="none" />
        <circle cx="176" cy="778" r="42" strokeWidth="1.3" strokeDasharray="4 6" />
        <circle cx="122" cy="806" r="26" strokeWidth="1.3" />
        <circle cx="146" cy="822" r="4" fill="#17E5DD" stroke="none" />
      </g>
    </svg>

    {/* back arc — workflow chips */}
    {chips.map((c) => (
      <div
        key={c.label.join(" ")}
        className="absolute rounded-[14px] border border-[#EAF0F2] bg-white"
        style={{
          left: c.x,
          top: c.y,
          width: c.w,
          height: c.h,
          transform: `rotate(${c.rot}deg) skewY(${c.rot * 0.6}deg)`,
          boxShadow: "0 22px 40px -26px rgba(16,42,52,0.35)",
        }}
      >
        <div className="flex h-full flex-col items-center justify-end gap-2 px-3 pb-5 text-[#0E1B1A]">
          <span className="text-[#0E1B1A]/80">{c.icon}</span>
          <div className="text-center text-[12.5px] font-medium leading-[1.25] text-[#1B2B2C]">
            {c.label.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        </div>
      </div>
    ))}

    {/* front — teal capability panels */}
    {panels.map((p) => (
      <div
        key={p.label.join(" ")}
        className="absolute rounded-[16px]"
        style={{
          left: p.x,
          top: p.y,
          width: p.w,
          height: p.h,
          transform: `skewY(${p.skew}deg)`,
          background: "linear-gradient(165deg, #17E5DD 0%, #05A79B 45%, #02857E 100%)",
          boxShadow:
            "0 40px 60px -34px rgba(2,133,126,0.55), 0 6px 14px -6px rgba(2,133,126,0.35)",
        }}
      >
        <div
          aria-hidden
          className="absolute inset-0 rounded-[16px]"
          style={{
            background:
              "linear-gradient(200deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 42%)",
          }}
        />
        <div className="relative flex h-full flex-col items-center justify-between px-3 py-7 text-white">
          <span className="opacity-95">{p.icon}</span>
          <div className="text-center text-[16px] font-semibold leading-[1.2]">
            {p.label.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ── capability card (left column) ────────────────────────────── */

const Card: React.FC<{
  label: string;
  title: string;
  body: string;
  icon: React.ReactNode;
}> = ({ label, title, body, icon }) => (
  <div className="flex flex-col rounded-[16px] border border-[#E9EEF1] bg-white px-6 py-5 shadow-[0_14px_34px_-28px_rgba(15,42,50,0.5)]">
    <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-[#02857E]/10 text-[#02857E]">
      {icon}
    </span>
    <h3 className="mt-3.5 text-[19px] font-bold leading-tight tracking-tight text-[#0E1B1A]">
      {label}
    </h3>
    <div className="mt-1 text-[14px] font-medium text-[#02857E]">{title}</div>
    <p className="mt-3 text-[14.5px] leading-[1.55] text-gray-600">{body}</p>
    <div className="flex-1" />
    <Link
      to="/shippers"
      className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-medium text-[#02857E] hover:underline"
    >
      Learn More <ArrowRight className="h-4 w-4" />
    </Link>
  </div>
);

/* ── section ──────────────────────────────────────────────────── */

const HomePlatform: React.FC = () => (
  <section className="relative overflow-hidden bg-[#FCFDFD] py-20 sm:py-24">
    <div className="relative mx-auto grid w-full max-w-[1540px] grid-cols-1 items-start gap-12 px-5 sm:px-8 lg:grid-cols-[minmax(0,46%)_minmax(0,54%)] lg:gap-10 xl:gap-12">
      {/* copy + cards */}
      <div>
        <div className="text-[13px] font-semibold uppercase tracking-[0.22em] text-[#02857E]">
          The LaneRunner Platform
        </div>
        <h2 className="mt-5 text-[36px] font-extrabold leading-[1.08] tracking-[-0.03em] text-[#0E1B1A] sm:text-[46px] lg:text-[50px]">
          Everything you need to source and move freight, in one place
          <span className="text-[#17E5DD]">.</span>
        </h2>
        <p className="mt-5 text-[18px] leading-relaxed text-gray-500 sm:text-[20px]">
          Your network. More capacity. Clearer pricing. One workflow.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Card
            label="Private Networks"
            title="Work with who you know"
            body="Bring the brokers and carriers you already trust onto one platform. Post freight directly to them, set your bid window, and keep every relationship yours."
            icon={<IUsers className="h-6 w-6" />}
          />
          <Card
            label="Open Marketplace"
            title="Add competition when you want it"
            body="Open any load to our vetted network of brokers and carriers. More qualified providers competing means better rates and better coverage."
            icon={<IGlobe className="h-6 w-6" />}
          />
          <Card
            label="Quote Comparison"
            title="See where the market really stands"
            body="Every quote lands in one view with low, average, and high. Compare side by side and award the right combination of price, capacity, and partner."
            icon={<IBars className="h-6 w-6" />}
          />
          <Card
            label="Shipment Management"
            title="From award to delivery"
            body="Dispatch details, documents, tracking, and messaging all live with the load — so nothing gets lost in an inbox after the freight is booked."
            icon={<ITruck className="h-6 w-6" />}
          />
        </div>
      </div>

      {/* isometric stage */}
      <div className="relative w-full lg:-mt-10">
        <div className="relative h-[340px] sm:h-[520px] lg:h-[640px] xl:h-[690px] 2xl:h-[750px]">
          <div className="absolute left-0 top-0 origin-top-left scale-[0.38] sm:left-1/2 sm:origin-top sm:-translate-x-1/2 sm:scale-[0.62] lg:left-0 lg:origin-top-left lg:translate-x-0 lg:scale-[0.76] xl:scale-[0.82] 2xl:scale-[0.9]">
            <Stage />
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HomePlatform;
